/**
 * Base Service Class for API Operations
 * Eliminates duplicate response handling, error extraction, and logging
 */

import apiClient from './apiClient';
import logger from '../../utils/logger';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

export abstract class BaseService {
  /**
   * Extract data from nested response structure
   * Handles multiple response formats
   */
  protected extractData<T>(response: any): T {
    // Handle different response structures
    if (response?.data?.data !== undefined) {
      return response.data.data;
    }
    if (response?.data !== undefined) {
      return response.data;
    }
    return response;
  }

  /**
   * Extract user-friendly error message from any error
   */
  protected getErrorMessage(error: any): string {
    // Try different error structures
    if (typeof error?.response?.data === 'string') {
      return error.response.data;
    }

    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'An unexpected error occurred'
    );
  }

  /**
   * GET request with error handling
   */
  protected async get<T>(url: string, config?: any): Promise<T> {
    try {
      logger.info(`GET ${url}`);
      const response = await apiClient.get(url, config);
      const data = this.extractData<T>(response);
      logger.success(`GET ${url} - Success`);
      return data;
    } catch (error) {
      logger.error(`GET ${url} failed`, error);
      throw error;
    }
  }

  /**
   * POST request with error handling
   */
  protected async post<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      logger.info(`POST ${url}`);
      const response = await apiClient.post(url, data, config);
      const result = this.extractData<T>(response);
      logger.success(`POST ${url} - Success`);
      return result;
    } catch (error) {
      logger.error(`POST ${url} failed`, error);
      throw error;
    }
  }

  /**
   * PATCH request with error handling
   */
  protected async patch<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      logger.info(`PATCH ${url}`);
      const response = await apiClient.patch(url, data, config);
      const result = this.extractData<T>(response);
      logger.success(`PATCH ${url} - Success`);
      return result;
    } catch (error) {
      logger.error(`PATCH ${url} failed`, error);
      throw error;
    }
  }

  /**
   * DELETE request with error handling
   */
  protected async delete<T>(url: string, config?: any): Promise<T> {
    try {
      logger.info(`DELETE ${url}`);
      const response = await apiClient.delete(url, config);
      const result = this.extractData<T>(response);
      logger.success(`DELETE ${url} - Success`);
      return result;
    } catch (error) {
      logger.error(`DELETE ${url} failed`, error);
      throw error;
    }
  }
}
